import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Bed, Bath, Car, Maximize, Tag } from 'lucide-react';
import Watermark from './Watermark';

const PropertyCard = ({ property }) => {
  const formatPrice = (price) => {
    // TODAS las propiedades mexicanas (Playa del Carmen, Monterrey) en MXN
    // Propiedades de Tulum en USD (mercado internacional)
    const ciudadesMexicanas = ['Playa del Carmen', 'Monterrey'];
    const esMexicana = ciudadesMexicanas.some(ciudad => 
      property.ciudad?.includes(ciudad)
    );
    
    // Propiedades mexicanas de VENTA: MXN
    if (esMexicana && property.tipo === 'venta') {
      return `$${new Intl.NumberFormat('es-MX').format(price)} MXN`;
    }
    
    // Propiedades de Tulum en VENTA: USD
    if (property.tipo === 'venta') {
      return `$${new Intl.NumberFormat('en-US').format(price)} USD`;
    }
    
    // Rentas: MXN
    return `$${new Intl.NumberFormat('es-MX').format(price)} MXN`;
  };

  return (
    <Link to={`/propiedad/${property.id}`}>
      <div className="bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden group">
        {/* Imagen con watermark */}
        <div className="relative h-64 overflow-hidden">
          <img
            src={property.fotos[0]}
            alt={property.titulo}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
          <Watermark />
          
          {/* Badge de tipo */}
          <div className="absolute top-4 left-4">
            <span className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide shadow-lg ${
              property.tipo === 'venta' 
                ? 'bg-gradient-to-r from-green-500 to-green-600 text-white' 
                : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
            }`}>
              {property.tipo}
            </span>
          </div>

          {/* Badge de tipo de propiedad */}
          <div className="absolute top-4 right-4">
            <span className="bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-semibold text-gray-800 shadow-lg flex items-center gap-1">
              <Tag size={12} className="text-amber-600" />
              {property.tipoPropiedad}
            </span>
          </div>
        </div>

        {/* Contenido */}
        <div className="p-5">
          {/* Precio */}
          <div className="mb-3">
            <p className="text-3xl font-bold text-blue-900 font-heading">
              {formatPrice(property.precio)}
              {property.tipo === 'renta' && <span className="text-base text-gray-500 font-body">/mes</span>}
            </p>
          </div>

          {/* Título */}
          <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2 group-hover:text-blue-900 transition-colors font-heading">
            {property.titulo}
          </h3>

          {/* Ubicación */}
          <div className="flex items-center gap-2 text-gray-600 mb-4">
            <MapPin size={16} className="text-amber-600 flex-shrink-0" />
            <span className="text-sm line-clamp-1 font-body">
              {property.colonia}, {property.ciudad}
            </span>
          </div>

          {/* Características */}
          {property.tipoPropiedad !== 'Terreno' && property.tipoPropiedad !== 'Local Comercial' && (
            <div className="flex flex-wrap gap-4 pt-4 border-t border-gray-200">
              {property.habitaciones > 0 && (
                <div className="flex items-center gap-2 text-gray-700">
                  <Bed size={18} className="text-blue-900" />
                  <span className="text-sm font-medium">{property.habitaciones}</span>
                </div>
              )}
              {property.banosCompletos > 0 && (
                <div className="flex items-center gap-2 text-gray-700">
                  <Bath size={18} className="text-blue-900" />
                  <span className="text-sm font-medium">{property.banosCompletos}</span>
                </div>
              )}
              {property.estacionamientos > 0 && (
                <div className="flex items-center gap-2 text-gray-700">
                  <Car size={18} className="text-blue-900" />
                  <span className="text-sm font-medium">{property.estacionamientos}</span>
                </div>
              )}
              {property.areaTotal > 0 && (
                <div className="flex items-center gap-2 text-gray-700">
                  <Maximize size={18} className="text-blue-900" />
                  <span className="text-sm font-medium">{property.areaTotal}m²</span>
                </div>
              )}
            </div>
          )}

          {property.tipoPropiedad === 'Terreno' && (
            <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
              <Maximize size={18} className="text-blue-900" />
              <span className="text-sm font-medium text-gray-700">{property.areaTotal}m²</span>
            </div>
          )}

          {/* CTA Button */}
          <button className="w-full mt-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white py-2.5 rounded-lg font-semibold hover:from-amber-600 hover:to-amber-700 transition-all shadow-md hover:shadow-lg">
            Ver Detalles
          </button>
        </div>
      </div>
    </Link>
  );
};

export default PropertyCard;
